import { useQueryClient } from "@tanstack/react-query";
import {
  AnalyticsComponentProvider,
  Button,
  FieldError,
  FileUploadTrigger,
  Flex,
  List,
  ProgressBar,
  SidePanel,
  Tag,
  Text,
  formatDisplayDate,
  scrollToInvalidField,
  useMediaQuery,
} from "@wayflyer/flyui";
import { IconHeadsetHelp20Line } from "@wayflyer/flyui-icons/20/line";
import { IconPage24Line } from "@wayflyer/flyui-icons/24/line";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { getAIRsDataQueryOptions, getAirPeriodSpan, getCoverageGapText, useErrorModal } from "shared-ui";

import { useSlideOnMove } from "../../../pages/banking/banking-accounts/accounts-list/use-slide-on-move";
import { MESSAGES as BANKING_MESSAGES } from "../../../pages/banking/banking-accounts/message-definitions";
import { StatementRow } from "../../../pages/banking/banking-accounts/components/statement-row";
import { SkipEntranceProvider } from "../../../pages/banking/banking-accounts/stagger-item/skip-entrance-provider";
import { StaggerItem } from "../../../pages/banking/banking-accounts/stagger-item/stagger-item";
import {
  capitalizeFirstLetter,
  formatInstitutionLabel,
  markGapMonthsReviewed,
} from "../../../pages/banking/banking-accounts/utils";
import { MonthsCoverageBar } from "../../../pages/banking/banking-accounts/months-coverage-bar/months-coverage-bar";
import { useAirBankStatementUpdateMutation } from "../../../queries/air-response/air-bank-statement.mutations";
import { useBankStatementSubmitMutation } from "../../../queries/bank-connect/bank-statement.mutations";
import { MESSAGES as BANK_CONTROLLER_MESSAGES } from "../../bank-controller-next/message-definitions";
import { UPLOAD_STATEMENTS_SIZE_LIMIT_MB } from "../../constants";
import { AIR_MESSAGES } from "../message-definitions";
import { getAcceptedFileTypes } from "../utils";

import { getAirRemainingRanges, getAirRequiredMonths, getAirStatementCoverage } from "./air-statement-coverage";
import { CannotProvideStatements } from "./cannot-provide-statements";
import { PANEL_MESSAGES } from "./message-definitions";
import { useBankStatementAirUploads } from "./use-bank-statement-air-uploads";

const MAX_ENTRANCE_INDEX = 3;

export interface BankStatementAirPanelProps {
  open: boolean;
  onClose: () => void;
  customerDomain: string;
  airPublicId: string;
  createdAt: string;
  bankData: FundingApplicationSchemas.BankStatementsForAccountAIRSchema;
  onContactSupport?: () => void;
}

const SUPPORT_CHAT_SELECTORS = ["#intercom-container", ".intercom-lightweight-app", "iframe[name^='intercom']"];

export const BankStatementAirPanel = ({
  open,
  onClose,
  customerDomain,
  airPublicId,
  createdAt,
  bankData,
  onContactSupport,
}: BankStatementAirPanelProps) => {
  const intl = useIntl();
  const isMobile = useMediaQuery()("sm").windowLte;
  const dialogRef = useRef<HTMLDivElement>(null);
  const actionsAnchorRef = useRef<HTMLDivElement>(null);
  const actionsSlideRef = useRef<HTMLDivElement>(null);
  const { openErrorModal } = useErrorModal();
  const queryClient = useQueryClient();

  const submittedObjectKeys = useRef(new Set<string>());
  const submitBankStatements = useBankStatementSubmitMutation(customerDomain);
  const updateBankStatementsAir = useAirBankStatementUpdateMutation(customerDomain);
  const uploads = useBankStatementAirUploads(customerDomain, bankData);

  const [cannotProvide, setCannotProvide] = useState(false);
  const [note, setNote] = useState("");
  const [confirmAttempt, setConfirmAttempt] = useState<{ kind: "coverage" | "reading"; inputs: string } | null>(null);

  const coverage = getAirStatementCoverage(bankData.periods, uploads.acceptedRanges, dayjs(), intl.locale);
  const bankName = bankData.bank_name || intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName);
  const title = formatInstitutionLabel(intl, bankData.bank_name ?? null, bankData.bank_account_mask ?? null);

  const remainingRanges = getAirRemainingRanges(bankData.periods, uploads.acceptedRanges);
  const coverageGapText = getCoverageGapText(intl, remainingRanges, getAirPeriodSpan(bankData.periods));
  const canCannotProvide = remainingRanges.length > 0;
  const isCannotProvide = cannotProvide && canCannotProvide;
  const cannotProvideLabel = intl.formatMessage(PANEL_MESSAGES.cannotProvideLabel);

  const currentInputs = [uploads.rows.length, uploads.uploadedObjectKeys.join(","), isCannotProvide, note].join("|");
  const attemptKind = confirmAttempt?.inputs === currentInputs ? confirmAttempt.kind : null;

  const formattedCreatedAt = `${formatDisplayDate(dayjs(createdAt))}, ${dayjs(createdAt).format("HH:mm")}`;

  useSlideOnMove(actionsAnchorRef, actionsSlideRef, uploads.rows.length);

  const canConfirm = isCannotProvide || coverage.isComplete;
  const confirmError =
    attemptKind === "reading" && uploads.isBusy
      ? intl.formatMessage(PANEL_MESSAGES.filesStillReadingError)
      : attemptKind === "coverage" && !canConfirm
      ? intl.formatMessage(PANEL_MESSAGES.incompleteCoverageError)
      : undefined;

  const handleClose = () => {
    setConfirmAttempt(null);
    onClose();
  };

  const handleConfirm = async () => {
    setConfirmAttempt(null);
    if (uploads.isBusy) {
      setConfirmAttempt({ kind: "reading", inputs: currentInputs });
      requestAnimationFrame(scrollToInvalidField);
      return;
    }
    if (!canConfirm) {
      setConfirmAttempt({ kind: "coverage", inputs: currentInputs });
      requestAnimationFrame(scrollToInvalidField);
      return;
    }

    const objectKeys = uploads.uploadedObjectKeys;
    try {
      const unsubmittedKeys = objectKeys.filter((key) => !submittedObjectKeys.current.has(key));
      if (unsubmittedKeys.length > 0) {
        await submitBankStatements.mutateAsync({
          object_keys: unsubmittedKeys,
          group:
            bankData.bank_name || (bankData.bank_account_mask ? `Account ${bankData.bank_account_mask}` : bankName),
        });
        unsubmittedKeys.forEach((key) => submittedObjectKeys.current.add(key));
      }
      await updateBankStatementsAir.mutateAsync({
        airPublicId,
        objectKeys,
        unableToProvideReason: isCannotProvide ? "other" : null,
        unableToProvideOtherText: isCannotProvide ? note.trim() || null : null,
        accountOpenDate: null,
        accountCloseDate: null,
      });
      onClose();
      queryClient.invalidateQueries({ queryKey: getAIRsDataQueryOptions(customerDomain).queryKey });
    } catch (e) {
      if (e instanceof Error) {
        openErrorModal({ variant: "submit-error", error: e });
      }
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={handleClose}
      title={title}
      variant="md"
      dialogRef={dialogRef}
      iconAction={
        onContactSupport
          ? {
              type: "action",
              icon: IconHeadsetHelp20Line,
              accessibilityLabel: intl.formatMessage(BANK_CONTROLLER_MESSAGES.contactSupportAction),
              onAction: onContactSupport,
            }
          : undefined
      }
      allowClickOutsideModalSelectors={SUPPORT_CHAT_SELECTORS}
      primaryAction={{
        content: intl.formatMessage(BANKING_MESSAGES.groupDetailsPanelConfirmButton),
        onAction: handleConfirm,
        loading: submitBankStatements.isPending || updateBankStatementsAir.isPending,
      }}
    >
      <AnalyticsComponentProvider component="bank-statement-air-panel">
        <SkipEntranceProvider>
          <Flex direction="column" gap="10" minHeight="100%">
            <Flex direction="column" gap="5">
              <Flex justify="space-between" align="flex-start" gap="3">
                <Flex direction="column" gap="1">
                  {bankData.bank_account_name && (
                    <Text size="lg" fontWeight="medium">
                      {capitalizeFirstLetter(bankData.bank_account_name)}
                    </Text>
                  )}
                </Flex>
                {uploads.isBusy ? (
                  <Tag content={intl.formatMessage(PANEL_MESSAGES.inProgressTag)} color="info" />
                ) : isCannotProvide ? (
                  <Tag content={intl.formatMessage(BANKING_MESSAGES.reviewedBadge)} color="info" />
                ) : coverage.isComplete ? (
                  <Tag content={intl.formatMessage(BANKING_MESSAGES.readyBadge)} color="positive" />
                ) : (
                  <Tag content={intl.formatMessage(BANKING_MESSAGES.toReviewBadge)} color="warning" />
                )}
              </Flex>
              {uploads.isBusy ? (
                <Flex direction="column" gap="2">
                  <Text>{intl.formatMessage(PANEL_MESSAGES.checkingUploads)}</Text>
                  <ProgressBar
                    value={uploads.progress}
                    maxValue={100}
                    aria-label={intl.formatMessage(PANEL_MESSAGES.uploadingStatements)}
                  />
                  {uploads.isReading ? (
                    <Text size="xs" color="subtle">
                      {intl.formatMessage(BANKING_MESSAGES.extractionProcessingText)}
                    </Text>
                  ) : (
                    <Flex justify="space-between">
                      <Text size="xs" color="subtle">
                        {intl.formatMessage(PANEL_MESSAGES.uploadingStatements)}
                      </Text>
                      <Text size="xs" color="subtle">
                        {intl.formatMessage(BANKING_MESSAGES.uploadInProgressPercentage, {
                          progress: uploads.progress,
                        })}
                      </Text>
                    </Flex>
                  )}
                  <FieldError name="bank-statement-air-confirm" error={confirmError} />
                </Flex>
              ) : (
                <>
                  {coverageGapText.kind === "fragmentedList" ? (
                    <Flex direction="column" gap="2">
                      <Text size="sm">{coverageGapText.intro}</Text>
                      <List>
                        {coverageGapText.periods.map((period, index) => (
                          <List.Item key={index}>
                            <Text size="sm">{period}</Text>
                          </List.Item>
                        ))}
                      </List>
                      <Text size="sm">{coverageGapText.outro}</Text>
                    </Flex>
                  ) : (
                    <Text size="sm">{coverageGapText.text}</Text>
                  )}
                  <MonthsCoverageBar
                    months={isCannotProvide ? markGapMonthsReviewed(coverage.months) : coverage.months}
                    animated
                  />
                  <FieldError name="bank-statement-air-confirm" error={confirmError} />
                </>
              )}
            </Flex>

            {uploads.rows.length > 0 && (
              <Flex direction="column" gap="4">
                <StaggerItem index={0} variant="fade">
                  <Flex gap="2">
                    <Text size="sm" fontWeight="semiBold">
                      {intl.formatMessage(BANKING_MESSAGES.sourcesSectionTitle)}
                    </Text>
                    <Text size="sm" color="subtle">
                      {uploads.rows.length}
                    </Text>
                  </Flex>
                </StaggerItem>
                <Flex direction="column" gap="2">
                  {uploads.rows.map((row, position) => (
                    <StaggerItem key={row.key} index={Math.min(position + 1, MAX_ENTRANCE_INDEX)}>
                      <StatementRow
                        label={row.label}
                        logo={<IconPage24Line />}
                        cardState={row.cardState}
                        subtitle={row.subtitle}
                        errorText={row.errorText}
                        isLoading={row.isLoading}
                        onRetry={row.onRetry}
                        onRemove={row.onRemove}
                      />
                    </StaggerItem>
                  ))}
                </Flex>
              </Flex>
            )}

            <div ref={actionsAnchorRef}>
              <div ref={actionsSlideRef}>
                <Flex direction="column" gap="4">
                  <FileUploadTrigger
                    onFilesAdded={uploads.onFilesAdded}
                    acceptedFileTypes={getAcceptedFileTypes(bankData.periods)}
                    sizeLimitMB={UPLOAD_STATEMENTS_SIZE_LIMIT_MB}
                    fullScreenDropHighlightContainerRef={dialogRef}
                    testId="bank-statement-air-panel-trigger"
                    trigger={({ open: openFilePicker, disabled }) => (
                      <Flex>
                        <Button
                          variant="Secondary"
                          size="small"
                          fullWidth={isMobile}
                          onClick={openFilePicker}
                          disabled={disabled}
                        >
                          {intl.formatMessage(BANKING_MESSAGES.sourcesAddStatementsButton)}
                        </Button>
                      </Flex>
                    )}
                  />

                  <CannotProvideStatements
                    label={cannotProvideLabel}
                    visible={canCannotProvide}
                    checked={isCannotProvide}
                    onCheckedChange={setCannotProvide}
                    note={note}
                    onNoteChange={setNote}
                    requiredMonths={getAirRequiredMonths(bankData.periods)}
                  />
                </Flex>
              </div>
            </div>

            <Flex grow={1} align="flex-end">
              <Text size="xs" color="subtle">
                {intl.formatMessage(PANEL_MESSAGES.createdFooter, { date: formattedCreatedAt })}
              </Text>
            </Flex>
          </Flex>
        </SkipEntranceProvider>
      </AnalyticsComponentProvider>
    </SidePanel>
  );
};
